import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, idr, idrShort } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Wallet, Landmark, PiggyBank, HandCoins, Receipt, Boxes, Activity, AlertTriangle, CheckCircle2, ArrowUpRight, ArrowDownRight, RefreshCw } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend, BarChart, Bar } from "recharts";
import { toast } from "sonner";
import { hariIniISO } from "../../lib/tanggal";

// Rekomendasi aksi per KPI — peta STATIS, bukan saran AI. Ambang tiap KPI di
// `ceo_dashboard.py` sudah deterministik (angka pasti, bukan penilaian yang perlu
// model bahasa), jadi teks & tautannya cukup dipetakan langsung di sini. Saran
// berbasis AI (kalau perlu narasi bebas) sudah ada jalurnya sendiri di
// `finance/ai_insight.py` — jangan duplikasi lewat LLM di sini, hasilnya cuma
// lebih lambat & tidak bisa diprediksi untuk sesuatu yang jawabannya sudah pasti.
//
// GP/NP margin, Cash Runway, Current/Cash Ratio TIDAK punya satu record tunggal
// yang bisa disalahkan — jadi tautannya jujur mengarah ke laporan (Laba Rugi /
// Buku Besar / Neraca), bukan pura-pura presisi ke satu transaksi.
const STATIC_KPI_ACTIONS = {
  gp_margin: {
    text: "Margin kotor di bawah standar sehat (≥25%). Tidak ada satu transaksi yang bisa disalahkan — periksa harga jual & HPP bulan ini di Laporan Laba Rugi.",
    to: "/finance/laporan",
    label: "Buka Laba Rugi",
  },
  np_margin: {
    text: "Margin bersih di bawah standar sehat (≥10%). Periksa efisiensi beban operasional di Laporan Laba Rugi.",
    to: "/finance/laporan",
    label: "Buka Laba Rugi",
  },
  cash_runway: {
    text: "Kas diperkirakan hanya cukup untuk waktu singkat pada laju burn rate 3 bulan terakhir. Tinjau mutasi kas & bank di Buku Besar.",
    to: "/finance/buku-besar",
    label: "Buka Buku Besar",
  },
  current_ratio: {
    text: "Aset lancar tidak cukup menutupi kewajiban jangka pendek (rasio <1,0). Tinjau posisi lengkap di Laporan → tab Neraca.",
    to: "/finance/laporan",
    label: "Buka Laporan (tab Neraca)",
  },
  cash_ratio: {
    text: "Kas & bank terlalu tipis dibanding utang lancar (rasio <0,2). Tinjau posisi lengkap di Laporan → tab Neraca.",
    to: "/finance/laporan",
    label: "Buka Laporan (tab Neraca)",
  },
};

const LAMPU_STYLE = {
  hijau:  { dot: "bg-emerald-500", ring: "ring-emerald-500/30", text: "text-emerald-700", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "SEHAT" },
  kuning: { dot: "bg-amber-500",    ring: "ring-amber-500/30",   text: "text-amber-700",   chip: "bg-amber-50 text-amber-700 border-amber-200",       label: "HATI-HATI" },
  merah:  { dot: "bg-rose-500",     ring: "ring-rose-500/30",    text: "text-rose-700",    chip: "bg-rose-50 text-rose-700 border-rose-200",         label: "AWAS" },
};
const BRAND_COLORS = ["#059669", "#0EA5E9", "#F59E0B", "#8B5CF6", "#EF4444", "#14B8A6", "#F97316", "#EC4899"];

const KpiCard = ({ icon: Icon, label, value, subtext, lampu, delay = 0, tid, action }) => {
  const st = LAMPU_STYLE[lampu] || LAMPU_STYLE.hijau;
  // Rekomendasi aksi hanya tampil saat status AWAS (merah) untuk kartu yang punya
  // lampu — itu momen yang diminta. Kartu tanpa lampu (mis. AP Jatuh Tempo, yang
  // sengaja tidak diberi ambang — lihat catatan di KpiCard AP) tetap boleh
  // menampilkan aksi selama `action` disediakan pemanggil.
  const showAction = !!action && (!lampu || lampu === "merah");
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      data-testid={tid}
      className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg grid place-items-center bg-slate-50 text-slate-700`}>
          <Icon className="w-5 h-5" />
        </div>
        {lampu && (
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.chip}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
            {st.label}
          </div>
        )}
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className={`font-display text-2xl font-bold ${lampu ? st.text : "text-slate-900"} truncate`}>{value}</p>
      {subtext && <p className="text-[11px] text-slate-500 mt-1">{subtext}</p>}
      {showAction && (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`${tid}-action`}>
          <p className="text-[11px] text-slate-600 leading-snug">{action.text}</p>
          {action.records && action.records.length > 0 && (
            <div className="mt-2 space-y-1">
              {action.records.map(rec => (
                <Link
                  key={rec.id} to={rec.to} data-testid={`${tid}-record-${rec.id}`}
                  className="flex items-center justify-between gap-2 text-[11px] px-2 py-1 rounded-md bg-slate-50 hover:bg-rose-50 hover:text-rose-700 text-slate-700 transition-colors"
                >
                  <span className="truncate">{rec.primary}</span>
                  <span className="text-slate-500 shrink-0 font-mono">{rec.secondary}</span>
                </Link>
              ))}
            </div>
          )}
          <Link
            to={action.to} data-testid={`${tid}-action-link`}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-900 hover:text-rose-700 hover:underline"
          >
            {action.label} <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
      )}
    </motion.div>
  );
};

const Panel = ({ title, subtitle, children, action, tid }) => (
  <div data-testid={tid} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
    <div className="flex items-start justify-between mb-4">
      <div>
        <p className="font-display font-bold text-base text-slate-900">{title}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

export default function FinanceDashboard() {
  const [data, setData] = useState(null);
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    (refresh ? setRefreshing : setLoading)(true);
    try {
      const [r, ag] = await Promise.all([
        api.get("/finance/dashboard/ceo?months=12"),
        api.get("/finance/piutang/aging-buckets"),
      ]);
      setData(r.data);
      setAging(ag.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat dashboard");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };
  useEffect(() => { load(false); }, []);

  if (loading) {
    return <div className="p-8 text-slate-500 text-sm">Memuat dashboard keuangan...</div>;
  }
  if (!data) return null;

  const { cash, laba_rugi_mtd: lr, rasio, ar_ap, inventory, trend_12m, brand_donut, top5_beban, health } = data;
  const monthLabel = new Date(data.tanggal).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const trendData = (trend_12m || []).map(t => ({
    label: t.periode.slice(5), omzet: t.omzet, laba: t.laba,
  }));

  // AR Overdue — status AWAS berarti ada piutang lewat tempo yang bisa langsung
  // ditautkan. Backend sudah mengirim top-5 record paling mendesak (paling lama
  // telat, lalu nominal terbesar) plus total hitungannya di `overdue_ar_count`.
  const arAction = (ar_ap.overdue_ar_count || 0) > 0 ? {
    text: `${ar_ap.overdue_ar_count} piutang sudah lewat jatuh tempo, total ${idrShort(ar_ap.overdue_ar)}. Tindak lanjuti penagihan berikut:`,
    to: "/finance/piutang?filter=jatuh_tempo",
    label: `Lihat Semua Piutang Jatuh Tempo (${ar_ap.overdue_ar_count})`,
    records: (ar_ap.top_overdue_piutang || []).map(p => ({
      id: p.id,
      primary: p.pelanggan_nama || "Tanpa nama",
      secondary: `${idr(p.sisa)} · telat ${p.hari_terlambat}h`,
      to: `/finance/piutang?filter=jatuh_tempo&highlight=${p.id}`,
    })),
  } : null;

  // AP Jatuh Tempo 7 Hari — kartu ini sengaja TIDAK diberi lampu (lihat alasan di
  // kartu "kpi-ap" di bawah), jadi aksi di sini tampil setiap kali ada utang yang
  // butuh perhatian, bukan cuma saat "AWAS". Tautan massal diarahkan ke filter
  // `jatuh_tempo` (sudah lewat tempo — irisan paling mendesak dari cakupan 7 hari
  // ini) kalau ada yang benar-benar telat; kalau belum ada yang telat, arahkan ke
  // daftar utang biasa karena tab "Jatuh Tempo" akan kosong. Tautan per-record
  // memakai filter=all supaya baris yang dituju pasti muncul apa pun status
  // jatuh temponya.
  const apAction = (ar_ap.ap_due_7d_count || 0) > 0 ? {
    text: `${ar_ap.ap_due_7d_count} utang jatuh tempo dalam 7 hari` +
      (ar_ap.ap_overdue_count > 0 ? ` (${ar_ap.ap_overdue_count} di antaranya sudah lewat tempo)` : "") +
      `, total ${idrShort(ar_ap.ap_due_7d)}. Siapkan dana pembayaran:`,
    to: ar_ap.ap_overdue_count > 0 ? "/finance/utang?filter=jatuh_tempo" : "/finance/utang",
    label: ar_ap.ap_overdue_count > 0 ? "Lihat Utang yang Sudah Lewat Tempo" : "Lihat Daftar Utang",
    records: (ar_ap.top_due_utang || []).map(u => ({
      id: u.id,
      primary: u.supplier_nama || "Tanpa nama",
      secondary: `${idr(u.sisa)} · ${u.hari_lagi < 0 ? `telat ${Math.abs(u.hari_lagi)}h` : u.hari_lagi === 0 ? "hari ini" : `${u.hari_lagi}h lagi`}`,
      to: `/finance/utang?filter=all&highlight=${u.id}`,
    })),
  } : null;

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-dashboard">
      {/* Header */}
      <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">CEO · Overview</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Kesehatan Keuangan</h1>
          <p className="text-sm text-slate-600 mt-1">Data live · Periode {monthLabel}</p>
        </div>
        <button
          onClick={() => load(true)} disabled={refreshing} data-testid="fin-btn-refresh"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Row 1: Cash & margins */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard tid="kpi-cash" icon={Wallet} label="Total Uang Perusahaan" value={idrShort(cash.total_cash)} subtext={`Bank ${idrShort(cash.total_bank)} · Kas ${idrShort(cash.total_kas)} · Petty ${idrShort(cash.total_petty)}`} delay={0} />
        <KpiCard tid="kpi-gp" icon={TrendingUp} label="Gross Profit Margin" value={`${lr.gp_margin_pct}%`} subtext={`Laba kotor ${idrShort(lr.laba_kotor)}`} lampu={health.gp_margin_color} action={STATIC_KPI_ACTIONS.gp_margin} delay={0.05} />
        <KpiCard tid="kpi-np" icon={Activity} label="Net Profit Margin" value={`${lr.np_margin_pct}%`} subtext={`Laba bersih ${idrShort(lr.laba_bersih)}`} lampu={health.np_margin_color} action={STATIC_KPI_ACTIONS.np_margin} delay={0.1} />
        <KpiCard tid="kpi-runway" icon={PiggyBank} label="Cash Runway" value={rasio.cash_runway_bulan >= 999 ? "∞" : `${rasio.cash_runway_bulan} bln`} subtext="Berdasarkan burn rate 3 bulan" lampu={health.cash_runway_color} action={STATIC_KPI_ACTIONS.cash_runway} delay={0.15} />
      </div>

      {/* Row 2: Ratios & AR/AP */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard tid="kpi-cr" icon={Landmark} label="Current Ratio" value={rasio.current_ratio >= 999 ? "∞" : rasio.current_ratio.toFixed(2)} subtext="Sehat jika > 1,5" lampu={health.current_ratio_color} action={STATIC_KPI_ACTIONS.current_ratio} delay={0.2} />
        <KpiCard tid="kpi-cashr" icon={Wallet} label="Cash Ratio" value={rasio.cash_ratio >= 999 ? "∞" : rasio.cash_ratio.toFixed(2)} subtext="Sehat jika > 0,5" lampu={health.cash_ratio_color} action={STATIC_KPI_ACTIONS.cash_ratio} delay={0.25} />
        <KpiCard tid="kpi-ar" icon={HandCoins} label="AR Overdue %" value={`${ar_ap.ar_overdue_pct}%`} subtext={`Overdue ${idrShort(ar_ap.overdue_ar)} dari ${idrShort(ar_ap.total_ar)}`} lampu={health.ar_overdue_color} action={arAction} delay={0.3} />
        {/* Kartu ini sengaja TANPA `lampu`/status AWAS — lihat catatan panjang di
            komentar `apAction` di atas untuk alasannya. Rekomendasi & tautan tetap
            tampil (lewat `action`) setiap ada utang yang jatuh tempo dalam 7 hari,
            terlepas dari warna. */}
        <KpiCard tid="kpi-ap" icon={Receipt} label="AP Jatuh Tempo 7 Hari" value={idrShort(ar_ap.ap_due_7d)} subtext={`Total utang aktif ${idrShort(ar_ap.total_ap)}`} action={apAction} delay={0.35} />
      </div>

      {/* Row 3: Trend + Aging Donut (Brand Donut moved below) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Panel tid="panel-trend" title="Tren Omzet & Laba 12 Bulan Terakhir" subtitle="Perbandingan pendapatan (hijau) vs laba bersih (biru)">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 6, right: 6, left: -8, bottom: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => idrShort(v)} />
                  <Tooltip formatter={(v) => idr(v)} contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#fff" }} />
                  <Line type="monotone" dataKey="omzet" name="Omzet" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="laba" name="Laba" stroke="#0EA5E9" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>
        {aging && <AgingDonutPanel aging={aging} />}
      </div>

      {/* Brand Donut standalone row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
        <Panel tid="panel-brand-donut" title="Komposisi Omzet per Brand" subtitle={`YTD ${new Date(data.tanggal).getFullYear()}`}>
          {brand_donut.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">Belum ada data brand</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={brand_donut} dataKey="omzet" nameKey="brand" innerRadius={44} outerRadius={80} paddingAngle={2}>
                    {brand_donut.map((_, i) => <Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => idr(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
        </div>
      </div>

      {/* Row 4: Top 5 Beban + Saldo Semua Akun */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel tid="panel-top5-beban" title="Top 5 Kategori Beban" subtitle="Bulan ini">
          {top5_beban.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">Belum ada beban bulan ini</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={top5_beban} layout="vertical" margin={{ top: 6, right: 6, left: 6, bottom: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => idrShort(v)} />
                  <YAxis type="category" dataKey="akun_name" tick={{ fontSize: 11, fill: "#334155" }} width={140} />
                  <Tooltip formatter={(v) => idr(v)} />
                  <Bar dataKey="total" fill="#EF4444" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
        <Panel tid="panel-saldo-akun" title="Saldo Semua Akun" subtitle={`${cash.bank.length} bank · ${cash.kas.length} kas · ${cash.petty_cash.length} petty`}
          action={<span className="text-sm font-display font-bold text-emerald-700">{idr(cash.total_cash)}</span>}>
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {[...cash.bank, ...cash.kas, ...cash.petty_cash].map(a => (
              <div key={a.id} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-100">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`w-7 h-7 rounded-md grid place-items-center ${a.category === "bank" ? "bg-sky-50 text-sky-600" : a.category === "petty_cash" ? "bg-purple-50 text-purple-600" : "bg-emerald-50 text-emerald-600"}`}>
                    {a.category === "bank" ? <Landmark className="w-4 h-4" /> : a.category === "petty_cash" ? <PiggyBank className="w-4 h-4" /> : <Wallet className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{a.name}</p>
                    <p className="text-[10px] text-slate-400 font-mono">{a.code} · {a.category}</p>
                  </div>
                </div>
                <p className={`text-sm font-display font-bold shrink-0 ${a.balance < 0 ? "text-rose-600" : "text-slate-800"}`}>{idr(a.balance)}</p>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Row 5: Inventory + Laba Rugi Mini */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard tid="kpi-inv-turn" icon={Boxes} label="Inventory Turnover" value={inventory.turnover_ratio.toFixed(2)} subtext={`Umur persediaan ~${inventory.umur_hari} hari`} delay={0} />
        <KpiCard tid="kpi-inv-value" icon={Boxes} label="Nilai Persediaan" value={idrShort(inventory.persediaan_total)} subtext="Stok unit + aksesoris" delay={0.05} />
        <KpiCard tid="kpi-pendapatan-mtd" icon={ArrowUpRight} label="Pendapatan Bulan Ini" value={idrShort(lr.pendapatan)} subtext="Total penjualan MTD" delay={0.1} />
        <KpiCard tid="kpi-beban-mtd" icon={ArrowDownRight} label="Beban Operasional MTD" value={idrShort(lr.beban)} subtext={`HPP ${idrShort(lr.hpp)}`} delay={0.15} />
      </div>

      {/* Row 6: Depresiasi Portfolio Projection */}
      {data.aset_projection_12m && data.aset_projection_12m.length > 0 && (
        <Panel tid="panel-aset-projection" title="Proyeksi Nilai Buku Aset Tetap · 12 Bulan"
          subtitle={`Portfolio ${data.aset_portfolio?.count_aktif || 0} aset aktif · Nilai buku sekarang ${idrShort(data.aset_portfolio?.current_book || 0)}`}
          action={<span className="text-xs text-slate-500">Auto-depreciation cron tiap tanggal 1</span>}>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.aset_projection_12m.map(p => ({ label: p.periode.slice(5), nilai_buku: p.nilai_buku_after, susut: p.penyusutan_bulan }))} margin={{ top: 6, right: 6, left: -8, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => idrShort(v)} />
                <Tooltip formatter={(v) => idr(v)} contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#fff", fontSize: 11 }} />
                <Line type="monotone" dataKey="nilai_buku" name="Nilai Buku Portfolio" stroke="#4F46E5" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      )}

      {/* Row 7: Ledger Automation & Reconciliation audit.
          KEPUTUSAN: sebelumnya komponen ini didefinisikan lengkap (termasuk tombol
          Auto-Fix & unduh PDF rekonsiliasi yang sudah tersambung ke endpoint asli
          di `finance/ledger_audit.py` & `finance/reconciliation_report.py`) tapi
          tidak pernah dirender — dead code. Dirender di sini karena ada gunanya
          nyata untuk CEO dashboard (rasio auto-posting & match rate rekonsiliasi
          mingguan) dan endpoint-nya sudah berfungsi. Tombol yang MENGUBAH data
          (Auto-Fix / Backfill, butuh izin `finance_audit.update`) digating di
          dalam komponennya sendiri lewat `useAuth().can()` supaya finance_staff /
          finance_auditor (yang hanya punya izin `view`) tidak melihat tombol yang
          akan ditolak backend dengan 403. */}
      <LedgerAuditPanel />
    </div>
  );
}

function AgingDonutPanel({ aging }) {
  const nonZero = aging.buckets.filter(b => b.total > 0);
  const pctMacet = aging.pct_macet || 0;
  const macetTone = pctMacet >= 15 ? "text-rose-700 bg-rose-50 border-rose-200" : pctMacet >= 5 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-emerald-700 bg-emerald-50 border-emerald-200";
  return (
    <Panel tid="panel-aging-donut" title="Aging Piutang" subtitle={`Per ${aging.as_of} · ${aging.count_total} piutang aktif`}
      action={<span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${macetTone}`}>Macet {pctMacet}%</span>}>
      {nonZero.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-400">Tidak ada piutang aktif</div>
      ) : (
        <>
          <div className="h-40 mb-2 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={nonZero} dataKey="total" nameKey="label" innerRadius={38} outerRadius={70} paddingAngle={2}>
                  {nonZero.map((b, i) => <Cell key={i} fill={b.color} />)}
                </Pie>
                <Tooltip formatter={(v) => idr(v)} contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#fff", fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                <p className="text-[9px] text-slate-500 uppercase font-semibold">Total</p>
                <p className="text-sm font-display font-bold text-slate-900">{idrShort(aging.total_aktif)}</p>
              </div>
            </div>
          </div>
          <div className="space-y-1">
            {aging.buckets.map(b => (
              <div key={b.key} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: b.color }} />
                <span className="text-slate-700 flex-1 truncate">{b.label}</span>
                <span className="text-slate-500 font-mono">{b.count}</span>
                <span className="text-slate-900 font-mono font-semibold w-20 text-right">{idrShort(b.total)}</span>
                <span className="text-slate-400 text-[10px] w-10 text-right">{b.pct}%</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

function LedgerAuditPanel() {
  const { can } = useAuth();
  // Auto-Fix/Backfill MENGUBAH data (repost jurnal) — backend mensyaratkan izin
  // `finance_audit.update` (deps.py: hanya finance_spv & finance_ceo yang punya;
  // finance_staff & finance_auditor cuma `view`). Tanpa gate ini, staff/auditor
  // melihat tombol yang pasti balas 403 saat diklik.
  const canFix = can("finance_audit", "update");
  const [d, setD] = useState(null);
  const [recon, setRecon] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [expDownload, setExpDownload] = useState(false);

  const loadAll = () => {
    api.get("/finance/audit/auto-vs-manual").then(r => setD(r.data)).catch(() => {});
    api.get("/finance/audit/reconciliation/summary?days=7").then(r => setRecon(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const autoFix = async (historical = false) => {
    const scope = historical ? "SEMUA HISTORICAL" : "30 hari terakhir";
    if (!window.confirm(`Repost semua missing sales/akuisisi/payroll — scope: ${scope}?`)) return;
    setFixing(true);
    try {
      const url = `/finance/audit/repost-missing?module=all&days=30${historical ? '&historical=true' : ''}`;
      const r = await api.post(url);
      toast.success(r.data.message + (r.data.total_fixed > 0 ? " · Notif dikirim ke Telegram 📢" : ""));
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal auto-fix"); }
    finally { setFixing(false); }
  };

  const downloadPdf = async () => {
    if (expDownload) return;
    setExpDownload(true);
    try {
      const token = localStorage.getItem("hris_token");
      const API = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
      const res = await fetch(`${API}/finance/audit/reconciliation/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      // `hariIniISO()`, bukan `.toISOString()` (UTC) — kosmetik (nama berkas saja).
      a.download = `reconciliation-${hariIniISO()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF Reconciliation terdownload ✓");
    } catch { toast.error("Gagal download PDF"); }
    finally { setExpDownload(false); }
  };

  if (!d) return <Panel title="Ledger Audit" subtitle="Loading..." tid="panel-audit">
    <div className="h-40 grid place-items-center text-slate-400 text-sm">Memuat ratio...</div>
  </Panel>;
  const pct = d.pct_auto_count || 0;
  const barTone = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500";
  const badgeTone = pct >= 80 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : pct >= 50 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-rose-50 text-rose-700 border-rose-200";
  const reconPct = recon?.combined?.pct_matched ?? 100;
  const reconTone = reconPct >= 90 ? "text-emerald-700" : reconPct >= 70 ? "text-amber-700" : "text-rose-700";

  return (
    <Panel tid="panel-audit" title="Ledger Automation & Reconciliation"
      subtitle={`Periode ${d.periode} · ${d.total_count} transaksi · Total ${idrShort(d.total_nominal)}`}
      action={<span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${badgeTone}`}>{pct}% auto</span>}>
      <div className="mb-4">
        <div className="flex justify-between text-[11px] text-slate-500 mb-1">
          <span>Auto-Posted {d.auto_count}</span>
          <span>Manual {d.manual_count}</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
          <div className={`${barTone} transition-all`} style={{ width: `${pct}%` }} data-testid="audit-bar-auto" />
          <div className="bg-slate-300 transition-all" style={{ width: `${100 - pct}%` }} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Nominal Auto</p>
          <p className="font-display font-bold text-lg text-emerald-700">{idrShort(d.auto_nominal)}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">{d.pct_auto_nominal}% dari total</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Nominal Manual</p>
          <p className="font-display font-bold text-lg text-slate-700">{idrShort(d.manual_nominal)}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">{(100 - d.pct_auto_nominal).toFixed(1)}% dari total</p>
        </div>
      </div>
      <div className="space-y-1.5 mb-4">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Breakdown per Sumber</p>
        {d.by_source.length === 0 ? (
          <p className="text-xs text-slate-400 py-2">Belum ada transaksi periode ini</p>
        ) : d.by_source.map(b => (
          <div key={b.key} className="flex items-center gap-2 text-xs">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: b.color }} />
            <span className="text-slate-700 flex-1 truncate">{b.label}</span>
            <span className="text-slate-500 font-mono">{b.count}×</span>
            <span className="text-slate-900 font-mono font-semibold w-20 text-right">{idrShort(b.nominal)}</span>
          </div>
        ))}
      </div>

      {/* Mini Reconciliation widget 7-day */}
      {recon && (
        <div className="border-t border-slate-100 pt-3 -mx-1">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Weekly Reconciliation (7 hari)</p>
              <p className={`font-display font-bold text-xl ${reconTone}`} data-testid="recon-match-rate">{reconPct}% <span className="text-xs font-normal text-slate-500">match rate</span></p>
            </div>
            <div className="flex gap-1.5 flex-wrap justify-end">
              <button onClick={downloadPdf} disabled={expDownload} data-testid="recon-download-pdf" className="text-[10px] px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium disabled:opacity-50">
                {expDownload ? "..." : "PDF"}
              </button>
              {/* Auto-Fix/Backfill MENGUBAH jurnal (repost). Butuh izin finance_audit.update
                  (SPV/CEO) — disembunyikan untuk staff/auditor supaya tidak menampilkan
                  tombol yang pasti ditolak backend dengan 403. */}
              {canFix && (
                <>
                  <button onClick={() => autoFix(false)} disabled={fixing} data-testid="recon-autofix" className="text-[10px] px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-50">
                    {fixing ? "Fixing..." : "🔧 Auto-Fix 30d"}
                  </button>
                  <button onClick={() => autoFix(true)} disabled={fixing} data-testid="recon-autofix-historical" title="Backfill SEMUA historical records (untuk migrasi awal)" className="text-[10px] px-2 py-1 rounded-md bg-slate-800 hover:bg-slate-900 text-white font-medium disabled:opacity-50">
                    {fixing ? "..." : "📚 Backfill All"}
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {recon.modules.map(m => (
              <div key={m.module} className="bg-slate-50 rounded-md p-2 text-center">
                <p className="text-[9px] uppercase text-slate-500 font-semibold">{m.label}</p>
                <p className="text-sm font-bold text-slate-900 font-display">{m.matched}<span className="text-slate-400">/{m.total}</span></p>
                {m.missing > 0 && <p className="text-[9px] text-rose-600 font-semibold mt-0.5">{m.missing} miss</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}
